import { useEffect, useState, useMemo } from "react";

function Users({ apiBase, token }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [gradeFilter, setGradeFilter] = useState("All");
    const [sortOrder, setSortOrder] = useState("newest");
    const [frameSort, setFrameSort] = useState("none");
    const [currentPage, setCurrentPage] = useState(1);
    const ROWS_PER_PAGE = 25;

    useEffect(() => {
        async function fetchUsers() {
            try {
                const response = await fetch(`${apiBase}/api/admin/users`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to load users");
                }

                const data = await response.json();
                setUsers(data);
            }
            catch (err) {
                setError(err.message);
            }
            finally {
                setLoading(false);
            }
        }
        if (token) {
            fetchUsers();
        }
    }, [apiBase, token]);

    useEffect(() => {
        setCurrentPage(1);
    }, [users, searchQuery, gradeFilter, sortOrder, frameSort]);

    const filteredUsers = useMemo(() => {
        let result = [...users];

        if (gradeFilter !== "All") {
            result = result.filter(u => u.grade === gradeFilter);
        }

        if (searchQuery.trim()) {
            const lowerQuery = searchQuery.toLowerCase().trim();
            result = result.filter(u =>
                (u.username || "").toLowerCase().includes(lowerQuery) ||
                (u.email || "").toLowerCase().includes(lowerQuery)
            );
        }

        result.sort((a, b) => {
            if (frameSort === "lots_asc") {
                return (a.lotsAttempted || 0) - (b.lotsAttempted || 0);
            }
            if (frameSort === "lots_desc") {
                return (b.lotsAttempted || 0) - (a.lotsAttempted || 0);
            }
            if (frameSort === "mots_asc") {
                return (a.motsAttempted || 0) - (b.motsAttempted || 0);
            }
            if (frameSort === "mots_desc") {
                return (b.motsAttempted || 0) - (a.motsAttempted || 0);
            }
            if (frameSort === "hots_asc") {
                return (a.hotsAttempted || 0) - (b.hotsAttempted || 0);
            }
            if (frameSort === "hots_desc") {
                return (b.hotsAttempted || 0) - (a.hotsAttempted || 0);
            }

            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();

            if (sortOrder === "newest") {
                return dateB - dateA;
            }
            else {
                return dateA - dateB;
            }
        });

        return result;
    }, [users, searchQuery, gradeFilter, sortOrder, frameSort]);

    const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE);
    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filteredUsers.slice(start, end);
    }, [filteredUsers, currentPage, ROWS_PER_PAGE]);

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="admin-panel">
            {error && <div className="admin-notice error">{error}</div>}
            <div className="admin-toolbar">
                <input
                    type="text"
                    placeholder="Search username or email..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
                <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}>
                    <option value="All">All Grades</option>
                    <option value="Grade 9">Grade 9</option>
                    <option value="Grade 10">Grade 10</option>
                    <option value="Grade 11">Grade 11</option>
                </select>
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                    <option value="newest">Newest Joined</option>
                    <option value="oldest">Oldest Joined</option>
                </select>
                <select value={frameSort} onChange={e => setFrameSort(e.target.value)}>
                    <option value="none">Sort by Frames</option>
                    <option value="lots_asc">LOT (Low to High)</option>
                    <option value="lots_desc">LOT (High to Low)</option>
                    <option value="mots_asc">MOT (Low to High)</option>
                    <option value="mots_desc">MOT (High to Low)</option>
                    <option value="hots_asc">HOT (Low to High)</option>
                    <option value="hots_desc">HOT (High to Low)</option>
                </select>
            </div>

            {filteredUsers.length > 0 && (
                <div className="pagination-controls">
                    <button className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Prev
                    </button>

                    <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                        &nbsp;·&nbsp;
                        {filteredUsers.length} rows total
                    </span>

                    <button className="pagination-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Username</th>
                            <th>Grade</th>
                            <th>Email Address</th>
                            <th>Phone Number</th>
                            <th>Joined On</th>
                            <th>LOT Frames</th>
                            <th>MOT Frames</th>
                            <th>HOT Frames</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedUsers.map((user, index) => (
                            <tr key={user.uid || user._id || index}>
                                <td>{((currentPage - 1) * ROWS_PER_PAGE) + index + 1}</td>
                                <td>{user.username || "—"}</td>
                                <td>{(user.grade?.replace(/^Grade\s*/i, "")) || "—"}</td>
                                <td>{user.email || "—"}</td>
                                <td>{user.phone || "—"}</td>
                                <td>{formatDate(user.createdAt)}</td>
                                <td>{user.lotsAttempted ?? 0}</td>
                                <td>{user.motsAttempted ?? 0}</td>
                                <td>{user.hotsAttempted ?? 0}</td>
                            </tr>
                        ))}
                        {loading && (
                            <tr>
                                <td colSpan={11}>Loading users...</td>
                            </tr>
                        )}
                        {!filteredUsers.length && !loading && (
                            <tr>
                                <td colSpan={11}>No users found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Users;